import React from 'react';
import { CONSTANTS } from '@tpco/tpco-saturn-types';
import { PvsCampaign, PvsCreative } from 'models/interfaces';
import { validateStartDate, validateEndDate } from '../ThomasCampaigns/ThomasCampaignValidation';
import { creativeIsPublishable } from './PvsCampaignValidation';

interface PvsCampaignPublishProps {
  thomasCampaignStatus: number;
  productFulfillment: any;
  campaign: PvsCampaign;
  creatives: PvsCreative[];
  publishCampaign(): any;
}

class PvsCampaignPublish extends React.Component<PvsCampaignPublishProps> {
  constructor(props: PvsCampaignPublishProps) {
    super(props);
  }

  canPublish = () => {
    if (this.props.thomasCampaignStatus === CONSTANTS.CAMPAIGN_STATUS.PUBLISH_IN_PROGRESS) {
      return false;
    }

    if (!this.startDateIsValid()) {
      return false;
    }

    if (!this.endDateIsValid()) {
      return false;
    }

    return true;
  };

  /**
   * Creative names must be unique across all existing creatives in an insertion
   * on Smart Ad Server
   */
  creativesNamesAreUnique = (creatives: PvsCreative[]) => {
    let namesAreUnique = true;

    const names = new Set();

    for (let i = 0; i < creatives.length; i++) {
      const creative = creatives[i];

      // only enforce for creatives that have been published
      // or are going to be published
      if (!creative.is_active && !creative.last_sync_date) {
        continue;
      }

      if (names.has(creative.name)) {
        namesAreUnique = false;
        break;
      }

      names.add(creative.name);
    }

    return namesAreUnique;
  };

  webCreativeIsValid = () => {
    const webCreatives = this.props.creatives.filter(
      x => x.platform_id === CONSTANTS.DISPLAY_AD_PLATFORM.WEB && x.is_active
    );

    console.log('webCreatives: ', webCreatives);

    const validCreatives = webCreatives.filter(x => creativeIsPublishable(x));

    if (validCreatives.length !== 1) {
      return false;
    }

    if (!this.creativesNamesAreUnique(this.props.creatives)) {
      return false;
    }

    return true;
  };

  mobileCreativeIsValid = () => {
    const mobileCreatives = this.props.creatives.filter(
      x => x.platform_id === CONSTANTS.DISPLAY_AD_PLATFORM.MOBILE && x.is_active
    );

    console.log('mobileCreatives: ', mobileCreatives);

    const validCreatives = mobileCreatives.filter(x => creativeIsPublishable(x));

    if (validCreatives.length !== 1) {
      return false;
    }

    if (!this.creativesNamesAreUnique(this.props.creatives)) {
      return false;
    }

    return true;
  };

  startDateIsValid = () => {
    const { campaign } = this.props;

    return !validateStartDate(campaign.start_date, {
      endDate: campaign.end_date
    });
  };

  endDateIsValid = () => {
    const { campaign } = this.props;

    return !validateEndDate(campaign.end_date, { startDate: campaign.start_date });
  };

  renderOk = () => {
    return (
      <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="icon">
        <title>ico-check-mark</title>
        <path
          d="M27.604 3.372a1.155 1.155 0 0 0-1.628.112L11.614 19.958 6.477 17.15a1.19 1.19 0 0 0-1.503 1.784l5.457 6.865a2.473 2.473 0 0 0 4.014-.198l.179-.278L27.816 4.867a1.153 1.153 0 0 0-.212-1.495z"
          fill="currentColor"
          fillRule="nonzero"
        />
      </svg>
    );
  };

  renderFail = () => {
    return (
      <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="icon">
        <title>ico-failed-retry</title>
        <path
          d="M29.546 25.249L17.898 5.083c-.644-1.112-2.254-1.112-2.898 0L3.351 25.249c-.644 1.112.147 2.517 1.434 2.517h23.298a1.676 1.676 0 0 0 1.463-2.517zm-13.463-13.99c.702-.176 1.405.175 1.727.819.117.234.146.498.117.732a53.757 53.757 0 0 0-.117 2.224c-.059 1.142-.147 2.312-.205 3.454-.03.351-.03.702-.059 1.083-.029.614-.497 1.083-1.112 1.083a1.12 1.12 0 0 1-1.112-1.054c-.088-1.785-.205-3.57-.293-5.356-.029-.468-.058-.966-.088-1.434 0-.703.469-1.376 1.142-1.551zm.38 13.404c-.82 0-1.463-.673-1.463-1.492 0-.82.673-1.493 1.493-1.493.82 0 1.463.673 1.463 1.522-.03.79-.702 1.463-1.493 1.463z"
          fill="currentColor"
          fillRule="nonzero"
        />
      </svg>
    );
  };

  getStatusClassName = (isValid: boolean, isRequired: boolean) => {
    if (isValid) {
      return 'child-campaign__status--valid';
    } else {
      if (isRequired) {
        return 'child-campaign__status--invalid';
      } else {
        return 'child-campaign__status--warning';
      }
    }
  };

  render() {
    const { campaign, publishCampaign, thomasCampaignStatus } = this.props;

    const status = CONSTANTS.CAMPAIGN_STATUS_MAP[thomasCampaignStatus];

    const webCreativeIsValid = this.webCreativeIsValid();
    const mobileCreativeIsValid = this.mobileCreativeIsValid();
    const startDateIsValid = this.startDateIsValid();
    const endDateIsValid = this.endDateIsValid();
    const canPublish = this.canPublish();

    return (
      <section className="card">
        <section className="thomas-campaign__status-container">
          <label>Publish Status: </label>
          <span className="thomas-campaign__status">{status}</span>
        </section>
        <section className="child-campaign__status-container">
          <div>
            <label>Campaign Name: </label>
            <span
              className={`child-campaign__status ${this.getStatusClassName(
                !!campaign.ad_server_name,
                true
              )}`}
            >
              {!!campaign.ad_server_name === true ? this.renderOk() : this.renderFail()}
            </span>
          </div>
          <div>
            <label>Start Date: </label>
            <span
              className={`child-campaign__status ${this.getStatusClassName(
                startDateIsValid,
                true
              )}`}
            >
              {startDateIsValid ? this.renderOk() : this.renderFail()}
            </span>
          </div>
          <div>
            <label>End Date: </label>
            <span
              className={`child-campaign__status ${this.getStatusClassName(endDateIsValid, true)}`}
            >
              {endDateIsValid ? this.renderOk() : this.renderFail()}
            </span>
          </div>
          <div>
            <label>Vertical: </label>
            <span
              className={`child-campaign__status ${this.getStatusClassName(
                !!campaign.vertical_id,
                true
              )}`}
            >
              {!!campaign.vertical_id === true ? this.renderOk() : this.renderFail()}
            </span>
          </div>
          <div>
            <label>Web Creative: </label>
            <span
              className={`child-campaign__status ${this.getStatusClassName(
                webCreativeIsValid,
                false
              )}`}
            >
              {webCreativeIsValid ? this.renderOk() : this.renderFail()}
            </span>
          </div>
          <div>
            <label>Mobile Creative: </label>
            <span
              className={`child-campaign__status ${this.getStatusClassName(
                mobileCreativeIsValid,
                false
              )}`}
            >
              {mobileCreativeIsValid ? this.renderOk() : this.renderFail()}
            </span>
          </div>
        </section>
        <div>
          <button
            className="btn btn-primary thomas-campaign__publish"
            type="button"
            disabled={!canPublish}
            onClick={publishCampaign}
          >
            Publish
          </button>
        </div>
      </section>
    );
  }
}

export default PvsCampaignPublish;
